import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MapPin, Navigation, CheckCircle2, XCircle, Phone, LogOut, Truck, User,
  Clock, Loader2, ChevronRight, Camera, Car, Bell, BellOff, ShieldAlert,
  CreditCard, HandCoins,
} from "lucide-react";
import { API } from "@/lib/api";
import {
  registerDriverServiceWorker, subscribeDriverToPush, pushSupported, playAlarm,
} from "@/lib/push";

const AUTH_KEY = "driver_auth";
const DRIVER_KEY = "driver_profile";

const money = (cents) => (cents ? `$${(cents / 100).toFixed(2)}` : "—");
const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

async function api(path, { method = "GET", body, auth } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(auth ? { "X-Driver-Auth": auth } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const serverMsg = data && (data.detail || data.message);
    if (serverMsg) throw new Error(serverMsg);
    if (res.status >= 500) throw new Error("Something went wrong — please try again or contact dispatch.");
    if (res.status === 401) throw new Error("Session expired — sign in again.");
    throw new Error(`Server ${res.status}`);
  }
  return data;
}

/* ==================== Invite-code sign in ==================== */

function InviteGate({ onAuth }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const data = await api("/nemt/driver/login", {
        method: "POST",
        body: { invite_code: code.trim().toUpperCase() },
      });
      localStorage.setItem(AUTH_KEY, data.token);
      localStorage.setItem(DRIVER_KEY, JSON.stringify(data.driver));
      onAuth(data.token, data.driver);
    } catch (err) {
      toast.error(err.message || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-brand-deep text-white flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center">
            <Truck className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-emerald-300 font-semibold">Driver</div>
            <div className="font-serif text-2xl">1st Touch Medical Transport</div>
          </div>
        </div>
        <form onSubmit={submit} data-testid="driver-login-form" className="bg-white/5 border border-white/10 rounded-3xl p-6">
          <label className="block text-xs uppercase tracking-[0.18em] text-white/60 mb-3">Invite code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            autoFocus
            maxLength={12}
            data-testid="driver-code-input"
            className="w-full px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white text-2xl tracking-[0.35em] font-mono text-center focus:outline-none focus:border-emerald-300"
          />
          <button
            type="submit"
            disabled={loading || !code.trim()}
            data-testid="driver-login-button"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 rounded-full transition disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Start shift"}
          </button>
          <p className="text-xs text-white/60 mt-5 text-center">
            Get your invite code from dispatch. It never expires — save it in your phone.
          </p>
          <p className="text-xs text-white/50 mt-3 text-center">
            Didn&apos;t receive your code? Ask dispatch to email it — they can send it from the driver row on the dispatcher dashboard.
          </p>
        </form>
        <p className="text-center text-xs text-white/40 mt-8">By signing in you agree to share your location with dispatch while on an active ride.</p>
      </div>
    </div>
  );
}

/* ==================== Onboarding wizard ==================== */

function OnboardingWizard({ auth, driver, onDone }) {
  const [ownership, setOwnership] = useState(driver.vehicle_ownership || "");
  const [carYear, setCarYear] = useState(driver.car_year || "");
  const [carMake, setCarMake] = useState(driver.car_make || "");
  const [carModel, setCarModel] = useState(driver.car_model || "");
  const [carColor, setCarColor] = useState(driver.car_color || "");
  const [carTag, setCarTag] = useState(driver.car_tag || "");
  const [bio, setBio] = useState(driver.bio || "");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [locationOk, setLocationOk] = useState(false);
  const [locationBusy, setLocationBusy] = useState(false);

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); return; }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const requestLocation = () => {
    setLocationBusy(true);
    if (!navigator.geolocation) { toast.error("This device does not support location"); setLocationBusy(false); return; }
    navigator.geolocation.getCurrentPosition(
      () => { setLocationOk(true); setLocationBusy(false); toast.success("Location enabled"); },
      (err) => {
        setLocationBusy(false);
        toast.error(err.code === err.PERMISSION_DENIED
          ? "Location denied — you must allow it to receive rides"
          : "Could not get location");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ownership) { toast.error("Please choose Independent Contractor or Employee"); return; }
    if (ownership === "own" && (!carMake || !carModel || !carTag)) {
      toast.error("Car make, model, and tag are required for your own vehicle");
      return;
    }
    setSaving(true);
    try {
      if (photoFile) {
        const form = new FormData();
        form.append("file", photoFile);
        const res = await fetch(`${API}/nemt/driver/photo`, {
          method: "POST",
          headers: { "X-Driver-Auth": auth },
          body: form,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Photo upload failed");
        }
      }
      await api("/nemt/driver/profile", {
        method: "PUT",
        auth,
        body: {
          vehicle_ownership: ownership,
          car_year: ownership === "own" && carYear ? parseInt(carYear, 10) : null,
          car_make: ownership === "own" ? carMake : null,
          car_model: ownership === "own" ? carModel : null,
          car_color: ownership === "own" ? (carColor || null) : null,
          car_tag: ownership === "own" ? carTag.toUpperCase() : null,
          bio: bio || null,
        },
      });
      toast.success("You're all set!");
      onDone();
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      <div className="bg-brand-deep text-white px-5 py-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Welcome, {driver.name}</div>
        <div className="font-serif text-2xl mt-1">Let&apos;s finish setting up your driver profile.</div>
        <p className="text-sm text-white/95 mt-2">Patients will see your photo, car info, and a short bio — helps them feel safe getting in.</p>
      </div>

      <form onSubmit={submit} data-testid="driver-onboarding-form" className="px-5 py-6 space-y-5 pb-32">
        {/* Vehicle ownership */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">How will you drive?</div>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => setOwnership("own")}
              data-testid="ownership-own"
              className={`text-left px-4 py-3 rounded-xl border-2 transition ${
                ownership === "own"
                  ? "border-brand-green bg-emerald-50"
                  : "border-brand-navy/15 hover:border-brand-navy"
              }`}
            >
              <div className="font-semibold text-brand-deep">Independent Contractor</div>
              <div className="text-xs text-brand-muted mt-0.5">I have my own vehicle. I&apos;ll enter its info below.</div>
            </button>
            <button
              type="button"
              onClick={() => setOwnership("company")}
              data-testid="ownership-company"
              className={`text-left px-4 py-3 rounded-xl border-2 transition ${
                ownership === "company"
                  ? "border-brand-green bg-emerald-50"
                  : "border-brand-navy/15 hover:border-brand-navy"
              }`}
            >
              <div className="font-semibold text-brand-deep">Employee</div>
              <div className="text-xs text-brand-muted mt-0.5">I&apos;ll drive a company fleet vehicle assigned by dispatch.</div>
            </button>
          </div>
        </div>

        {/* Photo */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">Profile photo</div>
          <label className="flex items-center gap-4 cursor-pointer">
            <div className="w-20 h-20 rounded-full bg-brand-surfaceAlt flex items-center justify-center overflow-hidden border-2 border-brand-navy/10">
              {photoPreview
                ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                : <Camera className="w-7 h-7 text-brand-muted" />}
            </div>
            <div>
              <div className="text-sm font-semibold text-brand-deep">{photoPreview ? "Change photo" : "Add photo"}</div>
              <div className="text-xs text-brand-muted">JPEG, PNG, or WebP · Max 5 MB</div>
            </div>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhoto} className="hidden" data-testid="driver-photo-input" />
          </label>
        </div>

        {/* Car info (only for independent contractors) */}
        {ownership === "own" && (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold flex items-center gap-2"><Car className="w-3.5 h-3.5" /> Your vehicle</div>
            <div className="grid grid-cols-2 gap-3">
              <input value={carYear} onChange={(e) => setCarYear(e.target.value)} inputMode="numeric" placeholder="Year" data-testid="car-year" className="px-3 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
              <input value={carMake} onChange={(e) => setCarMake(e.target.value)} placeholder="Make (Toyota)" data-testid="car-make" className="px-3 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
              <input value={carModel} onChange={(e) => setCarModel(e.target.value)} placeholder="Model (Camry)" data-testid="car-model" className="px-3 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
              <input value={carColor} onChange={(e) => setCarColor(e.target.value)} placeholder="Color" data-testid="car-color" className="px-3 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green" />
            </div>
            <input value={carTag} onChange={(e) => setCarTag(e.target.value.toUpperCase())} placeholder="License plate / tag" data-testid="car-tag" className="w-full px-3 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green font-mono tracking-wider uppercase" />
          </div>
        )}
        {ownership === "company" && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 text-sm text-blue-900" data-testid="company-vehicle-note">
            <div className="flex items-start gap-3">
              <Car className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Fleet vehicle assignment</div>
                <div className="text-xs mt-1">Dispatch will assign you a company vehicle in the driver dashboard. You don&apos;t need to enter car info here.</div>
              </div>
            </div>
          </div>
        )}

        {/* Bio */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">Short bio (optional)</div>
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={500} rows={3} placeholder="Retired paramedic, 20 years driving in Atlanta, love working with seniors..." data-testid="driver-bio" className="w-full px-3 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green resize-none" />
          <div className="text-xs text-brand-muted mt-1 text-right">{bio.length}/500</div>
        </div>

        {/* Location permission */}
        <div className={`rounded-2xl border p-5 ${locationOk ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-start gap-3">
            <MapPin className={`w-5 h-5 shrink-0 mt-0.5 ${locationOk ? "text-emerald-600" : "text-amber-600"}`} />
            <div className="flex-1">
              <div className={`text-sm font-semibold ${locationOk ? "text-emerald-900" : "text-amber-900"}`}>
                {locationOk ? "Location enabled ✓" : "Enable location — required to get rides"}
              </div>
              <div className="text-xs text-brand-muted mt-1">
                Dispatch uses your location to auto-route the closest driver to each pickup. Only while you&apos;re on an active ride.
              </div>
              {!locationOk && (
                <button type="button" onClick={requestLocation} disabled={locationBusy} data-testid="allow-location" className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-60">
                  {locationBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  Allow location while using app
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-brand-navy/10 px-5 py-4 shadow-[0_-4px_20px_rgba(0,0,0,0.04)]">
        <button
          onClick={submit}
          disabled={saving || !ownership || (ownership === "own" && (!carMake || !carModel || !carTag))}
          data-testid="onboarding-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 rounded-full transition disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Finish setup"}
        </button>
      </div>
    </div>
  );
}

/* ==================== Ride card + action button ==================== */

function ActionButton({ label, sublabel, onClick, loading, tone = "emerald", testid, disabled }) {
  const toneClasses = {
    emerald: "bg-emerald-500 hover:bg-emerald-600",
    amber: "bg-amber-500 hover:bg-amber-600",
    red: "bg-red-500 hover:bg-red-600",
    slate: "bg-slate-700 hover:bg-slate-800",
  }[tone];
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      data-testid={testid}
      className={`w-full flex items-center justify-between gap-3 ${toneClasses} text-white font-semibold py-5 px-6 rounded-2xl transition disabled:opacity-40 disabled:cursor-not-allowed shadow-lg`}
    >
      <div className="text-left">
        <div className="text-lg leading-tight">{label}</div>
        {sublabel && <div className="text-xs text-white/70 font-normal mt-0.5">{sublabel}</div>}
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ChevronRight className="w-6 h-6" />}
    </button>
  );
}

function ActiveRide({ ride, auth, onUpdate, onLogout }) {
  const [busy, setBusy] = useState(false);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [cashAck, setCashAck] = useState(false);
  const status = ride.driver_ride_status || "assigned";
  const [showWaitPrompt, setShowWaitPrompt] = useState(false);
  const [waitPromptCount, setWaitPromptCount] = useState(0);

  // Smart wait timer — nudge driver to update status once they've been at
  // pickup for a while but haven't started the ride. Respects the customer's
  // booked wait_minutes (e.g. dialysis, doctor visits) before starting the countdown.
  useEffect(() => {
    if (status !== "arrived" || !ride.driver_arrived_at) {
      setShowWaitPrompt(false);
      setWaitPromptCount(0);
      return;
    }
    const bookedWait = Math.max(0, Number(ride.wait_minutes) || 0);
    const firstThresholdMin = bookedWait + 15;
    const arrivedMs = new Date(ride.driver_arrived_at).getTime();
    const check = () => {
      const elapsedMin = (Date.now() - arrivedMs) / 60000;
      const nudgeCount = Math.max(0, Math.floor((elapsedMin - firstThresholdMin) / 15) + 1);
      if (elapsedMin >= firstThresholdMin && nudgeCount > waitPromptCount) {
        setShowWaitPrompt(true);
      }
    };
    check();
    const t = setInterval(check, 60000);
    return () => clearInterval(t);
  }, [status, ride.driver_arrived_at, ride.wait_minutes, waitPromptCount]);

  const dismissWaitPrompt = (thenAction) => {
    setShowWaitPrompt(false);
    setWaitPromptCount((n) => n + 1);
    if (thenAction) thenAction();
  };

  const call = async (path, opts) => {
    setBusy(true);
    try {
      await api(path, { method: "POST", auth, ...opts });
      await onUpdate();
    } catch (err) {
      const msg = err.message || "Action failed";
      // Payment hold not placed → show a long-lived red alert with dispatch #
      if (msg.toLowerCase().includes("payment hold not placed")) {
        toast.error(msg, { duration: 20000, id: "hold-missing" });
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const openMaps = (address) => {
    const q = encodeURIComponent(address || "");
    // Uses OS-native maps app on both iOS and Android
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank");
  };

  const displayStatus = status === "en_route" ? "On the way"
    : status === "dest_arrived" ? "At destination"
    : status === "returning" ? "Return trip"
    : status === "final_arrived" ? "At pickup"
    : titleCase(status);
  const isRoundTrip = !!(ride.return_trip && ride.return_trip !== "none") || !!ride.return_time;

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Sticky header */}
      <div className="bg-brand-deep text-white px-5 py-4 flex items-center justify-between sticky top-0 z-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Active ride</div>
          <div className="font-serif text-lg leading-tight">{ride.member_name}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-[0.12em] font-semibold px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            {displayStatus}
          </span>
        </div>
      </div>

      {/* Ride details */}
      <div className="px-5 py-6 space-y-4">
        {(status === "assigned" || status === "accepted") && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="upcoming-privacy-notice">
            <div className="flex items-start gap-2.5">
              <Clock className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-blue-800 font-bold">Upcoming ride</div>
                <div className="text-xs text-blue-900 mt-1 leading-relaxed">
                  This ride is <strong>scheduled</strong>. Your GPS location is <strong>not shared with dispatch</strong> until you tap <strong>Headed to pickup location</strong>. Once you do, dispatch, the patient, and any family/facility contacts can track your position live.
                </div>
              </div>
            </div>
          </div>
        )}

        {(status === "en_route" || status === "arrived" || status === "in_progress"
          || status === "dest_arrived" || status === "returning" || status === "final_arrived") && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="live-tracking-notice">
            <div className="flex items-start gap-2.5">
              <MapPin className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-800 font-bold">Live location sharing</div>
                <div className="text-xs text-emerald-900 mt-1 leading-relaxed">
                  Your GPS is <strong>live</strong> and visible to dispatch and the patient. Keep this screen open — location sharing is required to continue, arrive, or complete this ride.
                </div>
              </div>
            </div>
          </div>
        )}

        {ride.payment_mode === "on_arrival" && (
          <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-4" data-testid="on-arrival-driver-notice">
            <div className="flex items-start gap-2.5">
              <ShieldAlert className="w-5 h-5 text-orange-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-orange-800 font-bold">Pay-on-arrival ride</div>
                <div className="text-xs text-orange-900 mt-1 leading-relaxed">
                  <strong>Do not accept any payment from the passenger.</strong> If the passenger offers cash, Cash App, Zelle, or any other form of payment, <strong>contact dispatch immediately</strong> before accepting. All payment coordination is handled by 1st Touch dispatch — not by drivers in the field.
                </div>
                <a
                  href="tel:+14045904621"
                  data-testid="on-arrival-call-dispatch"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-orange-900 hover:text-orange-950 underline"
                >
                  <Phone className="w-3.5 h-3.5" /> Call dispatch: (404) 590-4621
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Trip type / mobility — no route $ (driver is on daily flat rate) */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">Trip</div>
            <div className="text-brand-deep font-serif text-xl leading-none mt-2">{titleCase(ride.trip_class)}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-brand-muted">{titleCase(ride.mobility || "")}</div>
            <div className="text-xs text-brand-muted mt-1">{ride.passengers || 1} pax</div>
          </div>
        </div>

        {/* Pickup */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold mb-1">Pickup</div>
          <div className="text-brand-deep font-medium leading-snug">{ride.pickup}</div>
          <div className="text-xs text-brand-muted mt-1">{ride.trip_date} · {ride.trip_time}</div>
          <button
            onClick={() => openMaps(ride.pickup)}
            data-testid="navigate-pickup"
            className="mt-3 inline-flex items-center gap-2 text-sm text-brand-green font-semibold hover:text-brand-navy transition"
          >
            <Navigation className="w-4 h-4" /> Navigate
          </button>
        </div>

        {/* Destination */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-navy font-semibold mb-1">Destination</div>
          <div className="text-brand-deep font-medium leading-snug">{ride.dropoff}</div>
          <button
            onClick={() => openMaps(ride.dropoff)}
            data-testid="navigate-dropoff"
            className="mt-3 inline-flex items-center gap-2 text-sm text-brand-green font-semibold hover:text-brand-navy transition"
          >
            <Navigation className="w-4 h-4" /> Navigate
          </button>
        </div>

        {/* Patient contact */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted font-semibold">Patient contact</div>
              <div className="text-brand-deep font-medium mt-1">{ride.member_name}</div>
              <div className="text-xs text-brand-muted">{ride.member_phone}</div>
            </div>
            <a
              href={`tel:${(ride.member_phone || "").replace(/[^0-9+]/g, "")}`}
              className="w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition"
              data-testid="call-patient"
            >
              <Phone className="w-5 h-5" />
            </a>
          </div>
          {ride.special_needs && (
            <div className="mt-3 pt-3 border-t border-brand-navy/10 text-xs text-brand-muted">
              <span className="font-semibold text-brand-deep">Notes:</span> {ride.special_needs}
            </div>
          )}
          {ride.notes && (
            <div className="mt-2 text-xs text-brand-muted italic">{ride.notes}</div>
          )}
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="sticky bottom-0 bg-white border-t border-brand-navy/10 px-5 py-4 shadow-[0_-4px_20px_rgba(0,0,0,0.04)] space-y-3">
        {status === "assigned" && (
          <>
            {ride.preferred_payment_method === "cash" && (
              <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-4" data-testid="cash-pickup-notice">
                <div className="flex items-start gap-2.5">
                  <HandCoins className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-amber-800 font-bold">Cash pickup required</div>
                    <div className="text-xs text-amber-900 mt-1 leading-relaxed">
                      The patient is paying <strong>cash{ride.hold_amount_cents ? ` (est. $${(ride.hold_amount_cents/100).toFixed(0)}+)` : ""}</strong>. Collect at <strong>{isRoundTrip ? "the return drop-off (original pickup address)" : "the destination"}</strong>. If you can't collect cash on this route, decline and dispatch will reassign.
                    </div>
                  </div>
                </div>
                <label className="flex items-start gap-2 mt-3 pt-3 border-t border-amber-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cashAck}
                    onChange={(e) => setCashAck(e.target.checked)}
                    data-testid="cash-ack-checkbox"
                    className="mt-0.5 w-4 h-4 accent-amber-600"
                  />
                  <span className="text-xs text-amber-900 font-medium">
                    I confirm I can collect cash on this trip.
                  </span>
                </label>
              </div>
            )}
            <ActionButton
              label="Accept ride"
              sublabel={titleCase(ride.trip_class)}
              onClick={() => call(`/nemt/driver/rides/${ride.id}/accept`)}
              loading={busy}
              tone="emerald"
              testid="btn-accept"
              disabled={ride.preferred_payment_method === "cash" && !cashAck}
            />
            <ActionButton
              label="Decline"
              sublabel="Send back to dispatch"
              onClick={() => call(`/nemt/driver/rides/${ride.id}/decline`)}
              loading={busy}
              tone="slate"
              testid="btn-decline"
            />
          </>
        )}
        {status === "accepted" && (
          <ActionButton
            label="Headed to pickup location"
            sublabel="Starts live tracking · notifies patient"
            onClick={() => call(`/nemt/driver/rides/${ride.id}/en-route`)}
            loading={busy}
            tone="emerald"
            testid="btn-en-route"
          />
        )}
        {status === "en_route" && (
          <ActionButton
            label="I've arrived at pickup"
            sublabel="Tap when you're at the patient"
            onClick={() => call(`/nemt/driver/rides/${ride.id}/arrived`)}
            loading={busy}
            tone="amber"
            testid="btn-arrived"
          />
        )}
        {status === "arrived" && (
          <ActionButton
            label="Start trip"
            sublabel="Patient is on board"
            onClick={() => call(`/nemt/driver/rides/${ride.id}/start`)}
            loading={busy}
            tone="emerald"
            testid="btn-start"
          />
        )}
        {status === "in_progress" && !isRoundTrip && (
          <>
            <SignaturePad rideId={ride.id} auth={auth} signed={!!ride.signature_data_url} />
            <ActionButton
              label="Complete ride"
              sublabel="Collect payment next"
              onClick={() => setShowPaymentScreen(true)}
              loading={busy}
              tone="emerald"
              testid="btn-complete"
              disabled={!ride.signature_data_url}
            />
          </>
        )}
        {status === "in_progress" && isRoundTrip && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="rt-in-progress-notice">
              <div className="text-[11px] uppercase tracking-[0.18em] text-blue-800 font-bold">Round trip · leg 1 of 2</div>
              <div className="text-xs text-blue-900 mt-1 leading-relaxed">
                Drive the patient to the destination. When you arrive, tap the button to capture the mid-trip signature. Do <strong>not</strong> tap Complete yet — the return leg is still ahead.
              </div>
            </div>
            <ActionButton
              label="Arrived at destination"
              sublabel="Round-trip · capture signature 1"
              onClick={() => call(`/nemt/driver/rides/${ride.id}/arrived-destination`)}
              loading={busy}
              tone="amber"
              testid="btn-arrived-destination"
            />
          </>
        )}
        {status === "dest_arrived" && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-amber-800 font-bold">Mid-trip signature</div>
              <div className="text-xs text-amber-900 mt-1 leading-relaxed">
                Please have {(ride.member_name || "the patient").split(" ")[0]} (or a family member) sign to confirm arrival at the destination. This is signature <strong>1 of 2</strong> for the round trip.
              </div>
            </div>
            <SignaturePad rideId={ride.id} auth={auth} signed={!!ride.dest_signature_data_url}
              endpoint={`/nemt/driver/rides/${ride.id}/dest-signature`}
              testidPrefix="dest-sig"
              label="Destination signature"
              onSaved={onUpdate} />
          </>
        )}
        {status === "returning" && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-blue-800 font-bold">Return leg · leg 2 of 2</div>
              <div className="text-xs text-blue-900 mt-1 leading-relaxed">
                Drive back to the original pickup address. When you arrive, tap the button below to capture the final signature and collect payment.
              </div>
            </div>
            <ActionButton
              label="Arrived at pickup"
              sublabel="Back at the original address"
              onClick={() => call(`/nemt/driver/rides/${ride.id}/final-arrived`)}
              loading={busy}
              tone="amber"
              testid="btn-final-arrived"
            />
          </>
        )}
        {status === "final_arrived" && (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
              <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-800 font-bold">Final signature · 2 of 2</div>
              <div className="text-xs text-emerald-900 mt-1 leading-relaxed">
                Please have {(ride.member_name || "the patient").split(" ")[0]} sign one more time to confirm safe drop-off, then collect payment.
              </div>
            </div>
            <SignaturePad rideId={ride.id} auth={auth} signed={!!ride.signature_data_url} />
            <ActionButton
              label="Collect payment"
              sublabel="Card on file · cash · Zelle"
              onClick={() => setShowPaymentScreen(true)}
              loading={busy}
              tone="emerald"
              testid="btn-collect-payment"
              disabled={!ride.signature_data_url}
            />
          </>
        )}
      </div>

      {showPaymentScreen && (
        <DriverPaymentScreen
          ride={ride}
          auth={auth}
          onClose={() => setShowPaymentScreen(false)}
          onDone={() => { setShowPaymentScreen(false); onUpdate(); }}
        />
      )}

      {/* Smart wait-time prompt */}
      {showWaitPrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="wait-prompt-modal">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-700 font-bold">
                  Status update needed
                </div>
                <h3 className="font-serif text-xl text-brand-deep mt-1 leading-tight">
                  Are you still waiting for {(ride.member_name || "the passenger").split(" ")[0]}?
                </h3>
                <p className="text-xs text-brand-muted mt-2 leading-relaxed">
                  You marked <strong>Arrived</strong> {Math.round((Date.now() - new Date(ride.driver_arrived_at).getTime()) / 60000)} min ago
                  {ride.wait_minutes ? ` (expected wait: ${ride.wait_minutes} min)` : ""}.
                  Please pick the option that matches what&rsquo;s happening.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => dismissWaitPrompt(() => call(`/nemt/driver/rides/${ride.id}/start`))}
                data-testid="wait-prompt-start"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-3 rounded-full transition"
              >
                <CheckCircle2 className="w-4 h-4" /> Passenger is on board — start ride
              </button>
              <button
                onClick={() => dismissWaitPrompt()}
                data-testid="wait-prompt-still-waiting"
                className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-amber-400 text-amber-800 hover:bg-amber-50 text-sm font-semibold py-3 rounded-full transition"
              >
                <Clock className="w-4 h-4" /> Still waiting — check again in 15 min
              </button>
              <a
                href="tel:+14045904621"
                onClick={() => dismissWaitPrompt()}
                data-testid="wait-prompt-call-dispatch"
                className="w-full inline-flex items-center justify-center gap-2 bg-white border border-brand-navy/15 text-brand-navy hover:border-brand-navy text-sm font-medium py-3 rounded-full transition"
              >
                <Phone className="w-4 h-4" /> Call dispatch — possible no-show
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== Signature pad ==================== */

function SignaturePad({ rideId, auth, signed, endpoint, testidPrefix = "sig", label = "Patient signature", onSaved }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [saved, setSaved] = useState(signed);
  const [busy, setBusy] = useState(false);
  const [signerName, setSignerName] = useState("");
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#003366";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width),
             y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const down = (e) => { e.preventDefault(); setDrawing(true); last.current = pos(e); };
  const move = (e) => {
    if (!drawing) return; e.preventDefault();
    const c = canvasRef.current, ctx = c.getContext("2d"), p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; setHasStrokes(true);
  };
  const up = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current, ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height); setHasStrokes(false);
  };
  const save = async () => {
    if (!hasStrokes) return;
    setBusy(true);
    try {
      const data = canvasRef.current.toDataURL("image/png");
      const path = endpoint || `/nemt/driver/rides/${rideId}/signature`;
      await api(path, {
        method: "POST", auth,
        body: { signature_data_url: data, signed_by_name: signerName || undefined },
      });
      setSaved(true);
      toast.success("Signature captured");
      onSaved && onSaved();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  if (saved) {
    return (
      <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-4 flex items-center gap-2" data-testid={`${testidPrefix}-saved`}>
        <CheckCircle2 className="w-4 h-4 text-emerald-700" />
        <span className="text-sm font-medium text-emerald-900">{label} captured.</span>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-4" data-testid={`${testidPrefix}-pad`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-2">{label}</div>
      <input
        value={signerName} onChange={(e) => setSignerName(e.target.value)}
        placeholder="Signer's name (patient or caregiver)"
        data-testid={`${testidPrefix}-name`}
        className="w-full px-3 py-2 mb-2 text-sm rounded-lg border border-brand-navy/15"
      />
      <div className="relative border-2 border-dashed border-brand-navy/20 rounded-lg bg-brand-surfaceAlt/30" style={{ touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          width={600} height={180}
          data-testid={`${testidPrefix}-canvas`}
          onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
          onTouchStart={down} onTouchMove={move} onTouchEnd={up}
          className="w-full h-40 rounded-lg"
        />
        {!hasStrokes && (
          <div className="absolute inset-0 flex items-center justify-center text-brand-muted text-xs pointer-events-none">
            Sign above
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <button onClick={clear} data-testid={`${testidPrefix}-clear`} className="text-xs text-brand-muted hover:text-brand-navy px-3 py-1.5 rounded-full">Clear</button>
        <button onClick={save} disabled={!hasStrokes || busy} data-testid={`${testidPrefix}-save`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark disabled:opacity-50 text-white text-sm font-medium py-2.5 rounded-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save signature
        </button>
      </div>
    </div>
  );
}


/* ==================== Driver-side payment collection screen ==================== */

function DriverPaymentScreen({ ride, auth, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [amount, setAmount] = useState("");
  const [offlineMethod, setOfflineMethod] = useState(ride.cash_approved ? "cash" : "zelle");
  const [offlineNote, setOfflineNote] = useState("");
  const [offlineRef, setOfflineRef] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/nemt/driver/rides/${ride.id}/payment-info`, {
          headers: { "X-Driver-Auth": auth },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to load payment info");
        setInfo(data);
        const suggested = data.final_fare_cents || data.hold_amount_cents || 0;
        setAmount(suggested ? (suggested / 100).toFixed(2) : "");
      } catch (err) {
        toast.error(err.message || "Failed to load payment info");
      } finally {
        setLoading(false);
      }
    })();
  }, [auth, ride.id]);

  const dollarsToCents = (v) => Math.round(parseFloat(v || "0") * 100);

  const chargeAndComplete = async () => {
    const cents = dollarsToCents(amount);
    if (cents < 100) return toast.error("Enter an amount of at least $1");
    setBusy("charge");
    try {
      await api(`/nemt/driver/rides/${ride.id}/set-final-fare`, {
        method: "POST", auth, body: { final_fare_cents: cents },
      });
      await api(`/nemt/driver/rides/${ride.id}/complete`, { method: "POST", auth });
      toast.success("Ride completed & card charged");
      onDone();
    } catch (err) {
      toast.error(err.message || "Charge failed");
    } finally {
      setBusy("");
    }
  };

  const markOfflineAndComplete = async () => {
    const cents = dollarsToCents(amount);
    if (cents < 0) return toast.error("Amount can't be negative");
    if ((offlineMethod === "zelle" || offlineMethod === "check") && !offlineRef.trim()) {
      return toast.error(`Enter the ${offlineMethod} confirmation number in the Reference # field.`);
    }
    setBusy("offline");
    try {
      await api(`/nemt/driver/rides/${ride.id}/set-final-fare`, {
        method: "POST", auth, body: { final_fare_cents: cents },
      });
      await api(`/nemt/driver/rides/${ride.id}/complete`, { method: "POST", auth });
      await api(`/nemt/driver/rides/${ride.id}/collect-offline`, {
        method: "POST", auth,
        body: {
          amount_cents: cents, method: offlineMethod,
          reference: offlineRef || undefined, note: offlineNote,
        },
      });
      toast.success(`Recorded $${(cents/100).toFixed(2)} via ${offlineMethod}`);
      onDone();
    } catch (err) {
      toast.error(err.message || "Failed to record payment");
    } finally {
      setBusy("");
    }
  };

  const sendNewCardLink = async () => {
    const cents = dollarsToCents(amount);
    if (cents < 100) return toast.error("Enter an amount of at least $1");
    setBusy("link");
    try {
      const res = await api(`/nemt/driver/rides/${ride.id}/send-payment-link`, {
        method: "POST", auth,
        body: { amount_cents: cents, origin_url: window.location.origin },
      });
      toast.success(res.email_sent
        ? "Payment link emailed to the patient — they'll enter their card"
        : "Link created — copy the URL below and text/hand it to the patient");
      if (res.checkout_url) {
        try { await navigator.clipboard.writeText(res.checkout_url); toast.info("Link copied"); } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.message || "Send link failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="driver-payment-screen">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-brand-deep text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Collect payment</div>
            <div className="font-serif text-xl leading-tight">{ride.member_name}</div>
          </div>
          <button onClick={onClose} data-testid="driver-payment-close" className="p-2 hover:bg-white/10 rounded-full">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-brand-muted"><Loader2 className="inline w-6 h-6 animate-spin" /></div>
        ) : !info ? null : (
          <div className="p-5 space-y-4">
            <div className="bg-brand-surfaceAlt rounded-xl p-4 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Card on file</div>
                  {info.has_saved_card ? (
                    <div className="mt-1 flex items-center gap-2 text-brand-deep font-medium">
                      <CreditCard className="w-4 h-4 text-brand-green" />
                      {(info.card_brand || "").toUpperCase()} ····{info.card_last4 || "····"}
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-red-700 font-semibold">No saved card — call dispatch</div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold">Hold placed</div>
                  <div className="font-serif text-lg text-brand-deep">${((info.hold_amount_cents || 0)/100).toFixed(2)}</div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-brand-muted font-semibold mb-2">Final trip total</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-brand-muted">$</span>
                <input
                  type="number" step="0.01" min="0" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="driver-final-amount"
                  className="w-full pl-7 pr-3 py-3 text-lg border border-brand-navy/15 rounded-lg text-brand-deep focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/15"
                />
              </div>
              <p className="text-[11px] text-brand-muted mt-1">Estimate: ${((info.estimate_low_cents || 0)/100).toFixed(0)}–${((info.estimate_high_cents || 0)/100).toFixed(0)}</p>
            </div>
            <button
              onClick={chargeAndComplete}
              disabled={!info.has_saved_card || busy === "charge"}
              data-testid="driver-charge-card"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold py-4 rounded-full transition"
            >
              {busy === "charge" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Charge card on file & complete
            </button>
            <button
              onClick={sendNewCardLink}
              disabled={busy === "link"}
              data-testid="driver-send-new-card-link"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark disabled:opacity-60 text-white text-sm font-semibold py-3 rounded-full transition"
            >
              {busy === "link" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Send new-card link (patient's phone)
            </button>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-bold mb-3">Or record cash / Zelle / check</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select
                  value={offlineMethod} onChange={(e) => setOfflineMethod(e.target.value)}
                  data-testid="driver-offline-method"
                  className="px-3 py-2 border border-amber-300 rounded-lg text-brand-deep bg-white text-sm"
                >
                  {ride.cash_approved && <option value="cash">Cash</option>}
                  <option value="zelle">Zelle</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="text"
                  placeholder={offlineMethod === "zelle" ? "Zelle confirmation #"
                    : offlineMethod === "check" ? "Check #"
                    : "Reference # (optional)"}
                  value={offlineRef} onChange={(e) => setOfflineRef(e.target.value)}
                  data-testid="driver-offline-reference"
                  className="px-3 py-2 border border-amber-300 rounded-lg text-brand-deep bg-white text-sm"
                />
              </div>
              <input
                type="text" placeholder="Note (optional)"
                value={offlineNote} onChange={(e) => setOfflineNote(e.target.value)}
                data-testid="driver-offline-note"
                className="w-full px-3 py-2 mb-3 border border-amber-300 rounded-lg text-brand-deep bg-white text-sm"
              />
              {(offlineMethod === "zelle" || offlineMethod === "check") && !offlineRef && (
                <div className="text-[11px] text-amber-800 mb-2">
                  ⚠️ Enter the {offlineMethod === "zelle" ? "Zelle confirmation" : "check"} number to complete.
                </div>
              )}
              <button
                onClick={markOfflineAndComplete}
                disabled={busy === "offline"}
                data-testid="driver-mark-offline"
                className="w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm font-semibold py-3 rounded-full transition"
              >
                {busy === "offline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
                Record payment & complete
              </button>
            </div>
            <a href="tel:+14045904621" data-testid="driver-payment-call-dispatch"
               className="block text-center text-xs text-brand-muted hover:text-brand-navy underline pt-2">
              Payment issue? Call dispatch (404) 590-4621
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== Idle (no active ride) screen ==================== */

function IdleScreen({ driver, onLogout, onRefresh, refreshing, pushStatus, onEnablePush, auth }) {
  const supported = pushSupported();
  const [earnings, setEarnings] = useState(null);
  const [vehCheck, setVehCheck] = useState(null); // { required, done }
  const [showCheck, setShowCheck] = useState(false);
  const [envelope, setEnvelope] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [e, v, env] = await Promise.all([
          api("/nemt/driver/earnings", { auth }),
          api("/nemt/driver/vehicle-check/today", { auth }).catch(() => ({ required: false, done: false })),
          api("/nemt/driver/cash-envelope", { auth }).catch(() => null),
        ]);
        if (!cancelled) { setEarnings(e); setVehCheck(v); setEnvelope(env); }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [auth]);
  return (
    <div className="min-h-screen bg-brand-bg">
      <div className="bg-brand-deep text-white px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Driver</div>
            <div className="font-serif text-lg leading-tight">{driver.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/manuals/driver.html" target="_blank" rel="noopener" data-testid="driver-manual-link" className="text-xs text-white/60 hover:text-white flex items-center gap-1.5">
            📘 Manual
          </a>
          <button onClick={onLogout} data-testid="driver-signout" className="text-xs text-white/60 hover:text-white flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> End shift
          </button>
        </div>
      </div>
      <div className="px-6 py-10">
        <div className="bg-white rounded-3xl border border-brand-navy/10 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-5">
            <Clock className="w-8 h-8 text-brand-green" />
          </div>
          <h1 className="font-serif text-2xl text-brand-deep">You&apos;re on shift.</h1>
          <p className="text-sm text-brand-muted mt-2">Dispatch will send rides here when they&apos;re assigned to you. Keep this app open.</p>
          <button
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="driver-refresh"
            className="mt-6 inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium px-5 py-2.5 rounded-full transition disabled:opacity-60"
          >
            <Loader2 className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Check for new rides
          </button>
        </div>

        {/* Vehicle check reminder */}
        {vehCheck?.required && !vehCheck?.done && (
          <div className="mt-5 bg-amber-50 border border-amber-300 rounded-2xl p-4 flex items-start justify-between gap-3" data-testid="veh-check-banner">
            <div>
              <div className="text-sm font-semibold text-amber-900">Daily vehicle check required</div>
              <div className="text-xs text-amber-800 mt-1">Log your fleet vehicle&apos;s miles, gas, and any damage before your first ride.</div>
            </div>
            <button onClick={() => setShowCheck(true)} data-testid="veh-check-open"
                    className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-2 rounded-full">
              Start check
            </button>
          </div>
        )}
        {vehCheck?.done && (
          <div className="mt-5 bg-emerald-50 border border-emerald-300 rounded-2xl p-3 text-xs text-emerald-900" data-testid="veh-check-done">
            ✓ Vehicle check completed today ({vehCheck.check?.miles} mi · {vehCheck.check?.gas_pct}% gas)
          </div>
        )}

        {/* Earnings — flat daily rate */}
        {earnings && (
          <div className="mt-5 bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="driver-earnings-card">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">My earnings</div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <div className="text-xs text-brand-muted">Today</div>
                <div className="font-serif text-2xl text-brand-deep" data-testid="earnings-today">
                  {money(earnings.earnings_today_cents)}
                </div>
                <div className="text-[11px] text-brand-muted mt-0.5">
                  {earnings.rides_today} ride{earnings.rides_today === 1 ? "" : "s"} today
                </div>
              </div>
              <div>
                <div className="text-xs text-brand-muted">Last 30 days</div>
                <div className="font-serif text-2xl text-brand-deep" data-testid="earnings-30d">
                  {money(earnings.earnings_30d_cents)}
                </div>
                <div className="text-[11px] text-brand-muted mt-0.5">
                  {earnings.days_worked_30d} day{earnings.days_worked_30d === 1 ? "" : "s"} worked · {earnings.rides_30d} rides
                </div>
              </div>
            </div>
            {earnings.daily_flat_rate_cents > 0 ? (
              <div className="mt-3 pt-3 border-t border-brand-navy/10 text-xs text-brand-muted">
                Daily rate: <strong className="text-brand-deep">{money(earnings.daily_flat_rate_cents)}</strong> — paid for each day you complete at least one ride.
              </div>
            ) : (
              <div className="mt-3 pt-3 border-t border-brand-navy/10 text-xs text-amber-700">
                Your daily rate hasn&apos;t been set yet. Ask dispatch to configure it.
              </div>
            )}
          </div>
        )}

        {/* Cash Envelope — end-of-shift reconciliation */}
        {envelope && envelope.total_owed_cents > 0 && (
          <div className="mt-5 rounded-2xl border-2 border-amber-400 bg-amber-50 p-5" data-testid="cash-envelope-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-amber-800 font-semibold">Cash envelope · today</div>
                <div className="font-serif text-3xl text-amber-900 mt-1">{money(envelope.total_owed_cents)}</div>
                <div className="text-[11px] text-amber-800 mt-0.5">Owed to the company · {envelope.count} collection{envelope.count === 1 ? "" : "s"}</div>
              </div>
              <HandCoins className="w-10 h-10 text-amber-700" />
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {["cash", "zelle", "check", "other"].map((m) => envelope.totals_by_method[m] > 0 && (
                <div key={m} className="bg-white rounded-lg border border-amber-200 p-2 text-center" data-testid={`envelope-total-${m}`}>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-amber-800 font-semibold">{m}</div>
                  <div className="font-serif text-lg text-amber-900">{money(envelope.totals_by_method[m])}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-amber-900 leading-relaxed border-t border-amber-300 pt-2 mt-1">
              Turn in <strong>{money(envelope.totals_by_method.cash || 0)}</strong> in cash to dispatch at end of shift.
              {envelope.totals_by_method.zelle > 0 && ` Zelle transfers already recorded — no action.`}
              {envelope.totals_by_method.check > 0 && ` Drop off checks at the office.`}
            </div>
            <details className="mt-3">
              <summary className="text-xs font-semibold text-amber-900 cursor-pointer" data-testid="envelope-details-toggle">
                See all {envelope.count} collection{envelope.count === 1 ? "" : "s"}
              </summary>
              <div className="mt-2 space-y-1.5 text-xs">
                {envelope.items.map((it, i) => (
                  <div key={i} className="bg-white rounded-lg border border-amber-200 p-2" data-testid={`envelope-item-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-brand-deep">{it.member_name}</span>
                      <span className="font-serif text-amber-900">{money(it.amount_cents)}</span>
                    </div>
                    <div className="text-[10px] text-brand-muted uppercase tracking-[0.12em] mt-0.5">
                      {it.method}{it.reference ? ` · Ref ${it.reference}` : ""}
                    </div>
                    {it.note && <div className="text-[11px] text-brand-muted mt-0.5">{it.note}</div>}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* Push notifications enrollment card */}
        {supported && (
          <div
            className={`mt-5 rounded-2xl border p-5 ${
              pushStatus === "granted"
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"
            }`}
            data-testid="driver-push-card"
          >
            <div className="flex items-start gap-3">
              {pushStatus === "granted"
                ? <Bell className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                : <BellOff className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
              <div className="flex-1">
                <div className={`text-sm font-semibold ${pushStatus === "granted" ? "text-emerald-900" : "text-amber-900"}`}>
                  {pushStatus === "granted" ? "Push alerts on" : "Turn on push alerts"}
                </div>
                <div className="text-xs text-brand-muted mt-1">
                  {pushStatus === "granted"
                    ? "You'll get a phone notification the moment dispatch assigns you a ride — even if the app is in the background."
                    : "Get an instant phone notification when a ride is assigned. No SMS charges. Sound + vibration included."}
                </div>
                {pushStatus !== "granted" && (
                  <button
                    onClick={onEnablePush}
                    data-testid="enable-push"
                    className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-full transition"
                  >
                    <Bell className="w-4 h-4" /> Enable notifications
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {!supported && (
          <div className="mt-5 bg-slate-50 border border-slate-200 rounded-2xl p-4 text-xs text-brand-muted">
            Your browser does not support push notifications. Keep this app open — a loud alarm will play if a ride is assigned while you&apos;re on shift.
          </div>
        )}
      </div>
      {showCheck && (
        <VehicleCheckModal
          auth={auth}
          onClose={() => setShowCheck(false)}
          onSaved={(c) => { setShowCheck(false); setVehCheck({ required: true, done: true, check: c }); }}
        />
      )}
    </div>
  );
}

function VehicleCheckModal({ auth, onClose, onSaved }) {
  const [miles, setMiles] = useState("");
  const [gas, setGas] = useState(75);
  const [damage, setDamage] = useState("");
  const [ok, setOk] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await api("/nemt/driver/vehicle-check", {
        method: "POST", auth,
        body: JSON.stringify({
          miles: Number(miles),
          gas_pct: Number(gas),
          damage_notes: damage || undefined,
          ok,
        }),
      });
      toast.success("Vehicle check saved");
      onSaved(d.check);
    } catch (err) { toast.error(err.message || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="veh-check-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-brand-bg rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Daily inspection</div>
            <h3 className="font-serif text-2xl text-brand-deep mt-1">Vehicle check</h3>
          </div>
          <button onClick={onClose} className="text-brand-muted hover:text-brand-navy"><XCircle className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-brand-deep block mb-1">Current mileage (odometer)</label>
            <input type="number" min={0} value={miles} onChange={(e) => setMiles(e.target.value)} required
                   data-testid="vc-miles" placeholder="e.g. 84523"
                   className="w-full px-4 py-3 rounded-lg border border-brand-navy/15" />
          </div>
          <div>
            <label className="text-xs font-medium text-brand-deep flex items-center justify-between mb-1">
              <span>Gas level</span>
              <span className="text-emerald-700 font-semibold">{gas}%</span>
            </label>
            <input type="range" min={0} max={100} step={5} value={gas} onChange={(e) => setGas(e.target.value)}
                   data-testid="vc-gas" className="w-full accent-brand-green" />
            <div className="flex justify-between text-[10px] text-brand-muted mt-1"><span>Empty</span><span>1/4</span><span>1/2</span><span>3/4</span><span>Full</span></div>
          </div>
          <div>
            <label className="text-xs font-medium text-brand-deep block mb-1">Damage / issue notes (optional)</label>
            <textarea value={damage} onChange={(e) => setDamage(e.target.value)} rows={3}
                      data-testid="vc-damage" placeholder="Any dents, warning lights, tires, cleanliness…"
                      className="w-full px-4 py-3 rounded-lg border border-brand-navy/15" />
          </div>
          <label className="flex items-center gap-2 text-sm text-brand-deep">
            <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} data-testid="vc-ok" className="accent-brand-green" />
            Vehicle is safe to drive today
          </label>
          <button type="submit" disabled={busy || !miles} data-testid="vc-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-medium py-3 rounded-full disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Submit check
          </button>
        </form>
      </div>
    </div>
  );
}

/* ==================== Main ==================== */

export default function Driver() {
  const [auth, setAuth] = useState(() => localStorage.getItem(AUTH_KEY) || "");
  const [driver, setDriver] = useState(() => {
    const s = localStorage.getItem(DRIVER_KEY);
    return s ? JSON.parse(s) : null;
  });
  const [activeRide, setActiveRide] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pushStatus, setPushStatus] = useState("default"); // default | granted | denied | unsupported
  const locationWatchIdRef = useRef(null);
  const prevRideIdRef = useRef(null);
  const alarmedRideIdsRef = useRef(new Set());

  // Swap in the driver-specific PWA manifest so "Add to Home Screen" installs at /driver
  useEffect(() => {
    const prev = document.querySelector('link[rel="manifest"]')?.getAttribute("href");
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    link.setAttribute("href", "/driver.webmanifest");
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta?.getAttribute("content");
    if (themeMeta) themeMeta.setAttribute("content", "#003366");
    return () => {
      if (prev) link.setAttribute("href", prev);
      if (themeMeta && prevTheme) themeMeta.setAttribute("content", prevTheme);
    };
  }, []);

  // Register service worker + check push permission
  useEffect(() => {
    if (!pushSupported()) { setPushStatus("unsupported"); return; }
    registerDriverServiceWorker();
    setPushStatus(Notification.permission);
  }, []);

  // Auto-subscribe on login if permission already granted
  useEffect(() => {
    if (!auth || !pushSupported() || Notification.permission !== "granted") return;
    subscribeDriverToPush(auth).catch(() => {});
  }, [auth]);

  const enablePush = async () => {
    if (!auth) return;
    const res = await subscribeDriverToPush(auth);
    setPushStatus(Notification.permission);
    if (res.ok) {
      toast.success("Notifications enabled — you'll get an alert on new rides.");
      // Fire a test push so the driver sees it working
      try {
        await fetch(`${API}/nemt/driver/push/test`, {
          method: "POST",
          headers: { "X-Driver-Auth": auth },
        });
      } catch (e) { /* silent */ }
    } else if (res.reason === "denied") {
      toast.error("Notifications blocked in your browser. Enable them in settings.");
    } else {
      toast.error("Could not turn on notifications — the app will still play an alarm.");
    }
  };

  const fetchMe = useCallback(async (showSpinner = false) => {
    if (!auth) return;
    if (showSpinner) setRefreshing(true);
    try {
      const data = await api("/nemt/driver/me", { auth });
      setDriver(data.driver);
      const newRide = data.active_ride;
      const prev = prevRideIdRef.current;
      // New ride assigned since last poll — play alarm + banner IF not already alarmed
      if (newRide && newRide.id && newRide.id !== prev &&
          newRide.driver_ride_status === "assigned" &&
          !alarmedRideIdsRef.current.has(newRide.id)) {
        alarmedRideIdsRef.current.add(newRide.id);
        playAlarm();
        toast.success(`New ride: ${newRide.pickup || "pickup"} — tap Accept to start.`, {
          duration: 15000,
          position: "top-center",
        });
      }
      prevRideIdRef.current = newRide ? newRide.id : null;
      setActiveRide(newRide);
    } catch (err) {
      if (String(err.message).includes("401") || String(err.message).toLowerCase().includes("invalid")) {
        localStorage.removeItem(AUTH_KEY);
        localStorage.removeItem(DRIVER_KEY);
        setAuth("");
        setDriver(null);
        setActiveRide(null);
      }
    } finally {
      if (showSpinner) setRefreshing(false);
    }
  }, [auth]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Poll every 15s while signed in for new assigned rides / status updates
  useEffect(() => {
    if (!auth) return;
    const t = setInterval(fetchMe, 15000);
    return () => clearInterval(t);
  }, [auth, fetchMe]);

  // Location tracking — starts on ANY active ride (assigned/accepted/…)
  // so the driver's location is fresh when they tap "Headed to pickup".
  // Backend rejects state changes if last_location is >2 min old.
  useEffect(() => {
    const trackable = activeRide && ["assigned", "accepted", "en_route", "arrived",
      "in_progress", "dest_arrived", "returning", "final_arrived"].includes(activeRide.driver_ride_status);
    if (!trackable) {
      if (locationWatchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
        locationWatchIdRef.current = null;
      }
      return;
    }
    if (!navigator.geolocation) return;
    if (locationWatchIdRef.current !== null) return;

    locationWatchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          await api("/nemt/driver/location", {
            method: "POST",
            auth,
            body: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          });
        } catch { /* silent — non-critical */ }
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          toast.error(
            "Location permission is REQUIRED to start, continue, or complete a ride. "
            + "Please enable location in your browser settings and reload.",
            { duration: 30000, id: "loc-denied" }
          );
        }
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 30000 }
    );
    return () => {
      if (locationWatchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
        locationWatchIdRef.current = null;
      }
    };
  }, [activeRide, auth]);

  const handleLogout = async () => {
    try { await api("/nemt/driver/logout", { method: "POST", auth }); } catch { /* ignore */ }
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(DRIVER_KEY);
    setAuth("");
    setDriver(null);
    setActiveRide(null);
  };

  if (!auth || !driver) {
    return <InviteGate onAuth={(t, d) => { setAuth(t); setDriver(d); }} />;
  }
  if (driver.onboarded === false) {
    return <OnboardingWizard auth={auth} driver={driver} onDone={() => fetchMe(true)} />;
  }
  if (activeRide) {
    return <ActiveRide ride={activeRide} auth={auth} onUpdate={fetchMe} onLogout={handleLogout} />;
  }
  return <IdleScreen driver={driver} onLogout={handleLogout} onRefresh={() => fetchMe(true)} refreshing={refreshing} pushStatus={pushStatus} onEnablePush={enablePush} auth={auth} />;
}
